import { createContext, useContext, useCallback, useRef, ReactNode } from 'react';

interface PullToRefreshContextType {
  registerRefresh: (callback: () => Promise<void>) => void;
  unregisterRefresh: () => void;
  triggerRefresh: () => Promise<void>;
}

const PullToRefreshContext = createContext<PullToRefreshContextType | null>(null);

export function PullToRefreshProvider({ children }: { children: ReactNode }) {
  const refreshCallbackRef = useRef<(() => Promise<void>) | null>(null);

  const registerRefresh = useCallback((callback: () => Promise<void>) => {
    refreshCallbackRef.current = callback;
  }, []);

  const unregisterRefresh = useCallback(() => {
    refreshCallbackRef.current = null;
  }, []);

  const triggerRefresh = useCallback(async () => {
    if (refreshCallbackRef.current) {
      await refreshCallbackRef.current();
    }
  }, []);

  return (
    <PullToRefreshContext.Provider value={{ registerRefresh, unregisterRefresh, triggerRefresh }}>
      {children}
    </PullToRefreshContext.Provider>
  );
}

/**
 * Hook for pages to register their refresh function.
 * Call this in any page component that has data to refresh.
 *
 * @example
 * function JobList() {
 *   const { setRefreshHandler } = usePullToRefresh();
 *
 *   useEffect(() => {
 *     setRefreshHandler(fetchData);
 *     return () => setRefreshHandler(null);
 *   }, []);
 * }
 */
export function usePullToRefresh() {
  const context = useContext(PullToRefreshContext);

  const setRefreshHandler = useCallback((handler: (() => Promise<void>) | null) => {
    if (!context) return;
    if (handler) {
      context.registerRefresh(handler);
    } else {
      context.unregisterRefresh();
    }
  }, [context]);

  return {
    setRefreshHandler,
    triggerRefresh: context?.triggerRefresh || (() => Promise.resolve()),
  };
}

export function usePullToRefreshContext() {
  return useContext(PullToRefreshContext);
}
